import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBackRounded, SaveRounded } from '@mui/icons-material';
import { useTenantStore, useTenantTimeZone } from '../../stores/tenant.store';
import { instantToZonedWall } from '@dosfilos/shared';
import { quizApi, type QuizQuestionInput } from '../../lib/api/assignment.api';
import { QuizQuestionsEditor } from './components/QuizQuestionsEditor';
import { TenantDateTimeField } from '../../components/TenantDateTimeField';
import { errorText } from '../../lib/error-text';

/** Returns an i18n key describing the first problem with the quiz, or null if OK. */
export function validateQuiz(questions: QuizQuestionInput[]): string | null {
  if (questions.length === 0) return 'assignmentsAdmin.quiz.errorNoQuestions';
  for (const q of questions) {
    const prompt = typeof q.prompt === 'string' ? q.prompt.trim() : '';
    if (!prompt) return 'assignmentsAdmin.quiz.errorNoPrompt';
    if (q.kind === 'single_choice') {
      if (q.options.some((o) => !o.text.trim())) return 'assignmentsAdmin.quiz.errorEmptyOption';
      if (!q.correctOptionId) return 'assignmentsAdmin.quiz.errorNoCorrect';
    } else if (q.kind === 'multiple_choice') {
      if (q.options.some((o) => !o.text.trim())) return 'assignmentsAdmin.quiz.errorEmptyOption';
      if (q.correctOptionIds.length === 0) return 'assignmentsAdmin.quiz.errorNoCorrect';
    } else if (q.kind === 'matching') {
      if (q.left.some((l) => !l.text.trim()) || q.right.some((r) => !r.text.trim())) {
        return 'assignmentsAdmin.quiz.errorEmptyOption';
      }
      if (q.correctPairs.length !== q.left.length) return 'assignmentsAdmin.quiz.errorNoCorrect';
    } else if (q.kind === 'short_answer') {
      if (q.acceptedAnswers.filter((a) => a.trim()).length === 0) {
        return 'assignmentsAdmin.quiz.errorNoAccepted';
      }
    } else if (q.kind === 'ordering') {
      if (q.items.length < 2) return 'assignmentsAdmin.quiz.errorNoQuestions';
      if (q.items.some((i) => !i.text.trim())) return 'assignmentsAdmin.quiz.errorEmptyOption';
    } else if (q.kind === 'numeric') {
      if (Number.isNaN(q.value)) return 'assignmentsAdmin.quiz.errorNumericValue';
    } else if (q.kind === 'cloze') {
      const blankIds = [...q.template.matchAll(/\[\[(\d+)\]\]/g)].map((m) => m[1]!);
      if (blankIds.length === 0) return 'assignmentsAdmin.quiz.errorClozeNoBlanks';
      for (const id of blankIds) {
        const accepted = q.blanks[id]?.acceptedAnswers?.filter((a) => a.trim()) ?? [];
        if (accepted.length === 0) return 'assignmentsAdmin.quiz.errorNoAccepted';
      }
    } else if (q.kind === 'open') {
      if (!(q.standard ?? '').trim()) return 'assignmentsAdmin.quiz.errorNoStandard';
    }
  }
  return null;
}

/** Trim + drop empty accepted answers just before sending (the editor keeps raw
 *  lines so the teacher can type freely, including trailing spaces). */
function sanitizeQuestions(questions: QuizQuestionInput[]): QuizQuestionInput[] {
  const clean = (arr: string[]) => arr.map((s) => s.trim()).filter(Boolean);
  return questions.map((q) => {
    if (q.kind === 'short_answer') return { ...q, acceptedAnswers: clean(q.acceptedAnswers) };
    if (q.kind === 'cloze') {
      return {
        ...q,
        blanks: Object.fromEntries(
          Object.entries(q.blanks).map(([id, b]) => [
            id,
            { ...b, acceptedAnswers: clean(b.acceptedAnswers) },
          ]),
        ),
      };
    }
    return q;
  });
}

/** Add absolute `ms` to a `<input datetime-local>` wall-clock (`yyyy-MM-ddTHH:mm`),
 *  returning a wall-clock. Kept in wall-clock space so the server converts once in
 *  the tenant zone; the browser tz cancels (built local, read local). */
function addMsToWall(wall: string, ms: number): string {
  const [datePart, timePart] = wall.split('T');
  const [y, mo, d] = datePart!.split('-').map(Number);
  const [h, mi] = (timePart ?? '00:00').split(':').map(Number);
  const t = new Date(new Date(y!, mo! - 1, d!, h!, mi!).getTime() + ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
}

/** Prefill carried from the "Nueva tarea" modal when the teacher picks Quiz. */
interface QuizBuilderPrefill {
  name?: string;
  description?: string;
  dueAt?: string;
  weight?: number;
}

export function QuizBuilderPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const tenantTz = useTenantTimeZone();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const prefill = (location.state as QuizBuilderPrefill | null) ?? {};

  const sectionId = searchParams.get('sectionId') ?? '';
  const { assignmentId } = useParams<{ assignmentId?: string }>();
  const isEdit = !!assignmentId;

  const [name, setName] = useState(prefill.name ?? '');
  const [description, setDescription] = useState(prefill.description ?? '');
  const [weight, setWeight] = useState(prefill.weight ?? 0);
  const [passPolicy, setPassPolicy] = useState<'sum' | 'threshold'>('sum');
  const [thresholdMetric, setThresholdMetric] = useState<'correctCount' | 'percent'>(
    'correctCount',
  );
  const [thresholdValue, setThresholdValue] = useState(1);
  // Lifecycle: publication (visible) → opening (answerable) → close.
  const [publishMode, setPublishMode] = useState<'now' | 'date'>('now');
  const [publishAtInput, setPublishAtInput] = useState('');
  const [openMode, setOpenMode] = useState<'publish' | 'date'>('publish');
  const [opensAtInput, setOpensAtInput] = useState('');
  const [closeMode, setCloseMode] = useState<'never' | 'date' | 'duration'>('never');
  const [closesAtInput, setClosesAtInput] = useState('');
  const [durationAmount, setDurationAmount] = useState(60);
  const [durationUnit, setDurationUnit] = useState<'minutes' | 'hours' | 'days'>('minutes');
  const [questions, setQuestions] = useState<QuizQuestionInput[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [hasSubmissions, setHasSubmissions] = useState(false);
  const [saved, setSaved] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  // Whether the quiz is already published. Drafts and brand-new quizzes are not.
  const [published, setPublished] = useState(false);
  // Assignment id: the route param, or the id returned by the first draft save.
  const [effectiveId, setEffectiveId] = useState<string | undefined>(assignmentId);

  // Serialize the whole builder form into an opaque draft blob (partial questions OK).
  const serializeDraft = (): Record<string, unknown> => ({
    name,
    description,
    weight,
    passPolicy,
    thresholdMetric,
    thresholdValue,
    publishMode,
    publishAtInput,
    openMode,
    opensAtInput,
    closeMode,
    closesAtInput,
    durationAmount,
    durationUnit,
    questions,
  });

  // Restore the form from a saved draft blob (best-effort; tolerates missing keys).
  const hydrateFromDraft = (d: Record<string, unknown>) => {
    const s = (k: string, fb = '') => (typeof d[k] === 'string' ? (d[k] as string) : fb);
    const n = (k: string, fb: number) => (typeof d[k] === 'number' ? (d[k] as number) : fb);
    setName(s('name'));
    setDescription(s('description'));
    setWeight(n('weight', 0));
    setPassPolicy(d['passPolicy'] === 'threshold' ? 'threshold' : 'sum');
    setThresholdMetric(d['thresholdMetric'] === 'percent' ? 'percent' : 'correctCount');
    setThresholdValue(n('thresholdValue', 1));
    setPublishMode(d['publishMode'] === 'date' ? 'date' : 'now');
    setPublishAtInput(s('publishAtInput'));
    setOpenMode(d['openMode'] === 'date' ? 'date' : 'publish');
    setOpensAtInput(s('opensAtInput'));
    setCloseMode(
      d['closeMode'] === 'date' ? 'date' : d['closeMode'] === 'duration' ? 'duration' : 'never',
    );
    setClosesAtInput(s('closesAtInput'));
    setDurationAmount(n('durationAmount', 60));
    const du = d['durationUnit'];
    setDurationUnit(du === 'hours' ? 'hours' : du === 'days' ? 'days' : 'minutes');
    setQuestions(Array.isArray(d['questions']) ? (d['questions'] as QuizQuestionInput[]) : []);
  };

  // Reflect a loaded/persisted quiz into the form state. Schedule fields fully
  // reset first, so a value cleared server-side (e.g. close removed) shows correctly.
  const applyQuiz = (q: Awaited<ReturnType<typeof quizApi.getForEdit>>) => {
    setPublished(q.published);
    setHasSubmissions(q.hasSubmissions);
    // An in-progress draft resumes from its snapshot, not the (empty) published def.
    if (q.draft) {
      hydrateFromDraft(q.draft);
      return;
    }
    setName(q.name);
    setDescription(typeof q.description === 'string' ? q.description : '');
    setWeight(q.weight);
    setPassPolicy(q.passPolicy ?? 'sum');
    if (q.passThreshold) {
      setThresholdMetric(q.passThreshold.metric);
      setThresholdValue(q.passThreshold.value);
    }
    setPublishMode(q.schedule?.publishAt ? 'date' : 'now');
    setPublishAtInput(
      q.schedule?.publishAt ? instantToZonedWall(q.schedule.publishAt, tenantTz) : '',
    );
    setOpenMode(q.schedule?.opensAt ? 'date' : 'publish');
    setOpensAtInput(q.schedule?.opensAt ? instantToZonedWall(q.schedule.opensAt, tenantTz) : '');
    setCloseMode(q.schedule?.closesAt ? 'date' : 'never');
    setClosesAtInput(q.schedule?.closesAt ? instantToZonedWall(q.schedule.closesAt, tenantTz) : '');
    setQuestions(q.questions);
  };

  // Edit mode: load the existing quiz (with its answer key) and pre-fill.
  useEffect(() => {
    if (!isEdit || !currentTenantId || !assignmentId) return;
    let cancelled = false;
    setLoadingEdit(true);
    quizApi
      .getForEdit({ tenantId: currentTenantId, assignmentId })
      .then((q) => {
        if (!cancelled) applyQuiz(q);
      })
      .catch((e) => {
        if (!cancelled) setErr(errorText(e));
      })
      .finally(() => {
        if (!cancelled) setLoadingEdit(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentTenantId, assignmentId]);

  const totalPoints = useMemo(
    () => questions.reduce((sum, q) => sum + (q.points || 0), 0),
    [questions],
  );

  const goBack = () =>
    navigate(sectionId ? `/curso/${sectionId}?tab=asignaciones` : '/assignments');

  const handleCreate = async () => {
    if (!currentTenantId || (!sectionId && !isEdit)) {
      setErr(t('assignmentsAdmin.quiz.errorNoSection'));
      return;
    }
    if (!name.trim()) {
      setErr(t('assignmentsAdmin.newDialog.nameRequired'));
      return;
    }
    const problem = validateQuiz(questions);
    if (problem) {
      setErr(t(problem));
      return;
    }

    // Send tenant-local WALL-CLOCK (yyyy-MM-ddTHH:mm) — the server converts to a UTC
    // instant in the tenant zone (toInstant). The inputs are already wall-clock, so
    // no browser-offset conversion here (that was the bug). Duration close is computed
    // from the effective open wall-clock (opensAt ?? publishAt ?? now-in-tenant-zone).
    const publishAt = publishMode === 'date' && publishAtInput ? publishAtInput : undefined;
    const opensAt = openMode === 'date' && opensAtInput ? opensAtInput : undefined;
    let closesAt: string | undefined;
    if (closeMode === 'date' && closesAtInput) {
      closesAt = closesAtInput;
    } else if (closeMode === 'duration') {
      const base = opensAt ?? publishAt ?? instantToZonedWall(new Date().toISOString(), tenantTz);
      const unitMs = { minutes: 60_000, hours: 3_600_000, days: 86_400_000 }[durationUnit];
      closesAt = addMsToWall(base, durationAmount * unitMs);
    }
    if (publishAt && opensAt && opensAt < publishAt) {
      setErr(t('assignmentsAdmin.quiz.errorOpenBeforePublish'));
      return;
    }
    if (opensAt && closesAt && closesAt < opensAt) {
      setErr(t('assignmentsAdmin.quiz.errorCloseBeforeOpen'));
      return;
    }
    const schedule =
      publishAt || opensAt || closesAt
        ? {
            ...(publishAt ? { publishAt } : {}),
            ...(opensAt ? { opensAt } : {}),
            ...(closesAt ? { closesAt } : {}),
          }
        : undefined;

    const quizPayload = {
      passPolicy,
      ...(passPolicy === 'threshold'
        ? { passThreshold: { metric: thresholdMetric, value: thresholdValue } }
        : {}),
      ...(schedule ? { schedule } : {}),
      questions: sanitizeQuestions(questions),
    };

    setSubmitting(true);
    setErr(null);
    setSaved(false);
    try {
      if (effectiveId) {
        // Editing an already-published quiz → save changes, stay. Publishing a draft
        // (not yet published) → publish now and leave.
        await quizApi.update({
          tenantId: currentTenantId,
          assignmentId: effectiveId,
          name: name.trim(),
          description: description.trim(),
          weight,
          quiz: quizPayload,
          ...(published ? {} : { publish: true }),
        });
        if (published) {
          const fresh = await quizApi.getForEdit({
            tenantId: currentTenantId,
            assignmentId: effectiveId,
          });
          applyQuiz(fresh);
          setSaved(true);
          return;
        }
        goBack();
        return;
      }
      await quizApi.create({
        tenantId: currentTenantId,
        sectionId,
        name: name.trim(),
        description: description.trim(),
        weight,
        publish: true,
        quiz: quizPayload,
      });
      goBack();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Save incomplete work as an unpublished draft. Lenient: only a section is needed
  // (for a brand-new draft); questions are NOT validated.
  const handleSaveDraft = async () => {
    if (!currentTenantId || (!sectionId && !effectiveId)) {
      setErr(t('assignmentsAdmin.quiz.errorNoSection'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    setDraftSaved(false);
    try {
      const res = await quizApi.saveDraft({
        tenantId: currentTenantId,
        ...(effectiveId ? { assignmentId: effectiveId } : { sectionId }),
        name: name.trim(),
        weight,
        draft: serializeDraft(),
      });
      setEffectiveId(res.id);
      // Reflect the new id in the URL so a refresh resumes the same draft.
      if (!effectiveId) navigate(`/assignments/quiz/${res.id}/edit`, { replace: true });
      setDraftSaved(true);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 6 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Button startIcon={<ArrowBackRounded />} onClick={goBack} disabled={submitting}>
          {t('assignmentsAdmin.quiz.backToAssignments')}
        </Button>
      </Stack>

      <Typography variant="h4" gutterBottom>
        {isEdit ? t('assignmentsAdmin.quiz.pageTitleEdit') : t('assignmentsAdmin.quiz.pageTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('assignmentsAdmin.quiz.pageSubtitle')}
      </Typography>

      {!sectionId && !isEdit && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('assignmentsAdmin.quiz.errorNoSection')}
        </Alert>
      )}
      {hasSubmissions && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('assignmentsAdmin.quiz.editHasSubmissions')}
        </Alert>
      )}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>
          {t('assignmentsAdmin.quiz.savedOk')}
        </Alert>
      )}
      {draftSaved && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDraftSaved(false)}>
          {t('assignmentsAdmin.quiz.draftSaved')}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('assignmentsAdmin.quiz.tabDatos')} />
        <Tab label={t('assignmentsAdmin.quiz.tabProgramacion')} />
        <Tab label={t('assignmentsAdmin.quiz.tabPreguntas', { count: questions.length })} />
      </Tabs>

      <Box hidden={tab !== 0}>
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <TextField
                label={t('assignmentsAdmin.newDialog.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
              <TextField
                label={t('assignmentsAdmin.newDialog.instructions')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('assignmentsAdmin.newDialog.weight')}
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(Number(e.target.value))}
                  sx={{ width: 120 }}
                />
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>{t('assignmentsAdmin.quiz.passPolicy')}</InputLabel>
                  <Select
                    label={t('assignmentsAdmin.quiz.passPolicy')}
                    value={passPolicy}
                    onChange={(e) => setPassPolicy(e.target.value as 'sum' | 'threshold')}
                  >
                    <MenuItem value="sum">{t('assignmentsAdmin.quiz.passPolicySum')}</MenuItem>
                    <MenuItem value="threshold">
                      {t('assignmentsAdmin.quiz.passPolicyThreshold')}
                    </MenuItem>
                  </Select>
                  <FormHelperText>{t('assignmentsAdmin.quiz.passPolicyHelp')}</FormHelperText>
                </FormControl>
              </Stack>

              {passPolicy === 'threshold' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>{t('assignmentsAdmin.quiz.thresholdMetric')}</InputLabel>
                    <Select
                      label={t('assignmentsAdmin.quiz.thresholdMetric')}
                      value={thresholdMetric}
                      onChange={(e) =>
                        setThresholdMetric(e.target.value as 'correctCount' | 'percent')
                      }
                    >
                      <MenuItem value="correctCount">
                        {t('assignmentsAdmin.quiz.thresholdCorrectCount')}
                      </MenuItem>
                      <MenuItem value="percent">
                        {t('assignmentsAdmin.quiz.thresholdPercent')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    type="number"
                    label={t('assignmentsAdmin.quiz.thresholdValue')}
                    value={thresholdValue}
                    onChange={(e) => setThresholdValue(Math.max(0, Number(e.target.value)))}
                    sx={{ width: 140 }}
                  />
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box hidden={tab !== 1}>
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2.5 }}>
              {t('assignmentsAdmin.quiz.scheduleTitle')}
            </Typography>
            <Stack spacing={2}>
              {/* Publication */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>{t('assignmentsAdmin.quiz.publication')}</InputLabel>
                  <Select
                    label={t('assignmentsAdmin.quiz.publication')}
                    value={publishMode}
                    onChange={(e) => setPublishMode(e.target.value as 'now' | 'date')}
                  >
                    <MenuItem value="now">{t('assignmentsAdmin.quiz.publishNow')}</MenuItem>
                    <MenuItem value="date">{t('assignmentsAdmin.quiz.publishOnDate')}</MenuItem>
                  </Select>
                </FormControl>
                {publishMode === 'date' && (
                  <TenantDateTimeField
                    label={t('assignmentsAdmin.quiz.publishAt')}
                    value={publishAtInput}
                    onChange={setPublishAtInput}
                  />
                )}
              </Stack>
              {/* Opening */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>{t('assignmentsAdmin.quiz.opening')}</InputLabel>
                  <Select
                    label={t('assignmentsAdmin.quiz.opening')}
                    value={openMode}
                    onChange={(e) => setOpenMode(e.target.value as 'publish' | 'date')}
                  >
                    <MenuItem value="publish">{t('assignmentsAdmin.quiz.openAtPublish')}</MenuItem>
                    <MenuItem value="date">{t('assignmentsAdmin.quiz.openOnDate')}</MenuItem>
                  </Select>
                </FormControl>
                {openMode === 'date' && (
                  <TenantDateTimeField
                    label={t('assignmentsAdmin.quiz.opensAt')}
                    value={opensAtInput}
                    onChange={setOpensAtInput}
                  />
                )}
              </Stack>
              {/* Closing */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>{t('assignmentsAdmin.quiz.closing')}</InputLabel>
                  <Select
                    label={t('assignmentsAdmin.quiz.closing')}
                    value={closeMode}
                    onChange={(e) => setCloseMode(e.target.value as 'never' | 'date' | 'duration')}
                  >
                    <MenuItem value="never">{t('assignmentsAdmin.quiz.closeNever')}</MenuItem>
                    <MenuItem value="date">{t('assignmentsAdmin.quiz.closeOnDate')}</MenuItem>
                    <MenuItem value="duration">{t('assignmentsAdmin.quiz.closeDuration')}</MenuItem>
                  </Select>
                </FormControl>
                {closeMode === 'date' && (
                  <TenantDateTimeField
                    label={t('assignmentsAdmin.quiz.closesAt')}
                    value={closesAtInput}
                    onChange={setClosesAtInput}
                  />
                )}
                {closeMode === 'duration' && (
                  <>
                    <TextField
                      type="number"
                      label={t('assignmentsAdmin.quiz.durationAmount')}
                      value={durationAmount}
                      onChange={(e) => setDurationAmount(Math.max(1, Number(e.target.value)))}
                      sx={{ width: 120 }}
                    />
                    <FormControl sx={{ width: 140 }}>
                      <InputLabel>{t('assignmentsAdmin.quiz.durationUnit')}</InputLabel>
                      <Select
                        label={t('assignmentsAdmin.quiz.durationUnit')}
                        value={durationUnit}
                        onChange={(e) =>
                          setDurationUnit(e.target.value as 'minutes' | 'hours' | 'days')
                        }
                      >
                        <MenuItem value="minutes">
                          {t('assignmentsAdmin.quiz.unitMinutes')}
                        </MenuItem>
                        <MenuItem value="hours">{t('assignmentsAdmin.quiz.unitHours')}</MenuItem>
                        <MenuItem value="days">{t('assignmentsAdmin.quiz.unitDays')}</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box hidden={tab !== 2}>
        <Card variant="outlined">
          <CardContent>
            <QuizQuestionsEditor value={questions} onChange={setQuestions} />
          </CardContent>
        </Card>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1} />
        <Typography variant="caption" color="text.secondary">
          {t('assignmentsAdmin.quiz.totalPoints', { count: totalPoints })}
        </Typography>
        <Button onClick={goBack} disabled={submitting}>
          {t('assignmentsAdmin.newDialog.cancel')}
        </Button>
        {/* Draft save: only while the quiz is not yet published. */}
        {!published && (
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={submitting || loadingEdit || (!sectionId && !effectiveId)}
          >
            {t('assignmentsAdmin.quiz.saveDraft')}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<SaveRounded />}
          onClick={handleCreate}
          disabled={submitting || loadingEdit || (!sectionId && !effectiveId)}
        >
          {submitting
            ? t('assignmentsAdmin.newDialog.creating')
            : published
              ? t('assignmentsAdmin.quiz.saveChanges')
              : t('assignmentsAdmin.quiz.publish')}
        </Button>
      </Stack>
    </Box>
  );
}
